import type { InternalProduct } from 'components/discovery/product/tile-parts/tile-parts-typing.js'
import { orderTemplateQuantity, orderTemplateTrackingContext } from 'components/discovery/product/add-to-order-templates/order-template-payload.js'
import type { SimpleWidgetCfg, WidgetType } from './base-product-tile-types.js'
import { renameDeprecatedWidgetProperties } from './deprecated-widget-properties.js'
import 'components/discovery/product/tile-parts/parts/code/tile-code.js'
import 'components/discovery/product/tile-parts/parts/variants/tile-variants.js'
import 'components/discovery/product/tile-parts/parts/variant-indication/tile-variant-indication.js'
import 'components/discovery/product/tile-parts/parts/tags/tile-tags.js'
import 'components/discovery/product/tile-parts/parts/title/tile-title.js'
import 'components/discovery/compare/components/compare-checkbox/compare-checkbox.js'
import 'components/discovery/product/plp-cta-button/plp-cta-button.js'

export interface WidgetRenderContext {
  product: InternalProduct
  locale: string
  index?: number | undefined
}

const KNOWN_WIDGET_TYPES: readonly WidgetType[] = [
  'csc-product-tile-add-to-order-template',
  'csc-brand',
  'csc-product-tile-title',
  'csc-product-tile-product-code',
  'csc-price',
  'csc-retail-price',
  'csc-product-tile-variant-indication',
  'csc-product-tile-variants',
  'csc-featured-attributes',
  'csc-stock-indication',
  'csc-product-tile-tags',
  'csc-plp-cta-button',
  'csc-order-form',
  'csc-compare-checkbox',
  'csc-quotation-add-product',
  'csc-levies',
  'csc-product-rating',
  'csc-order-template-slide-in',
]

function levenshteinDistance(a: string, b: string): number {
  let previousRow: number[] = Array.from({ length: b.length + 1 }, (_, j) => j)
  for (let i = 1; i <= a.length; i++) {
    const currentRow: number[] = [i]
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      currentRow.push(Math.min(
        (currentRow[j - 1] ?? 0) + 1,
        (previousRow[j] ?? 0) + 1,
        (previousRow[j - 1] ?? 0) + cost
      ))
    }
    previousRow = currentRow
  }
  return previousRow[b.length] ?? 0
}

function findClosestWidgetType(type: string): WidgetType | null {
  let closest: WidgetType | null = null
  let closestDistance = Infinity
  for (const knownType of KNOWN_WIDGET_TYPES) {
    const distance = levenshteinDistance(type, knownType)
    if (distance < closestDistance) {
      closestDistance = distance
      closest = knownType
    }
  }
  return closestDistance <= 10 ? closest : null
}

export function createWidgetElement(type: WidgetType): HTMLElement | null {
  if ((KNOWN_WIDGET_TYPES as readonly string[]).includes(type)) {
    return document.createElement(type)
  }

  if (typeof type !== 'string' || !type) {
    console.warn(
      `csc-base-product-tile: a widget entry in tile-config is missing its "type" (or it isn't a string) — got ${JSON.stringify(type)}. ` +
      'Every widget needs a "type", e.g. { "type": "csc-price" }. If you meant to conditionally omit a widget, remove the whole entry from the array instead of leaving an empty placeholder.'
    )
    return null
  }

  const suggestion = findClosestWidgetType(type)
  console.warn(
    `csc-base-product-tile: unknown widget type "${type}" in tile-config.` +
    (suggestion ? ` Did you mean "${suggestion}"?` : '') +
    ` Valid types are: ${KNOWN_WIDGET_TYPES.join(', ')}.`
  )
  return null
}

export function toAttributeName(key: string): string {
  return key.replace(/[A-Z]/g, (ch) => `-${ch.toLowerCase()}`)
}

export function getWidgetProperties(w: Pick<SimpleWidgetCfg, 'type' | 'properties'>, ctx: WidgetRenderContext): Record<string, any> {
  const { product } = ctx
  const configProps: Record<string, any> = { locale: ctx.locale, ...renameDeprecatedWidgetProperties(w.type, w.properties) }

  if (w.type === 'csc-order-form') {
    const { uom, uomSource, ...restConfigProps } = configProps
    const uomValue = uom ?? uomSource
    const resolvedUom = uomValue === 'sale_uom' ? product.ordering.saleUom
      : uomValue === 'default_uom' ? product.ordering.defaultUom
        : uomValue
    const orderQuantity = product.ordering.orderQuantityDec ?? product.ordering.orderQuantity
    const configuredDefault = product.ordering.defaultOrderQuantityDec ?? product.ordering.defaultOrderQuantity
    return {
      productId: product.id,
      ...(product.ordering.minimumOrderQuantity != null && { min: product.ordering.minimumOrderQuantity }),
      ...(product.ordering.maximumOrderQuantity != null && { max: product.ordering.maximumOrderQuantity }),
      ...(product.pageLink && { pageLink: product.pageLink }),
      step: orderQuantity,
      default: configuredDefault ?? orderQuantity,
      ...(resolvedUom !== undefined && { uom: resolvedUom }),
      ...restConfigProps
    }
  }

  if (w.type === 'csc-order-template-slide-in') {
    return {
      trackingProduct: product,
      productId: product.id,
      quantity: orderTemplateQuantity(product),
      trackingContext: orderTemplateTrackingContext(ctx.index),
      ...configProps
    }
  }

  if (w.type === 'csc-compare-checkbox') {
    return {
      productId: product.id,
      productName: product.name,
      productCode: product.code,
      productImage: product.picture.mini,
      ...configProps
    }
  }

  if (w.type === 'csc-quotation-add-product') {
    return {
      productId: product.id,
      ...configProps
    }
  }

  return configProps
}

export function buildWidgetElement(w: SimpleWidgetCfg, ctx: WidgetRenderContext): HTMLElement | null {
  const widgetElement = createWidgetElement(w.type)
  if (!widgetElement) return null

  const props = getWidgetProperties(w, ctx)
  Object.entries(props).forEach(([key, value]) => {
    if (w.type === 'csc-order-form' && key === 'uom') return
    const attr = toAttributeName(key)
    if (typeof value === 'boolean') {
      // Set the property directly, not just the attribute: widgetElement is always a freshly
      // created, not-yet-upgraded element here, so an attribute that was never present can't be
      // "removed" to turn a true-by-default boolean off — removeAttribute on an absent attribute
      // never fires attributeChangedCallback, since nothing actually changed, leaving the
      // property stuck at its constructor default. Setting the property directly works
      // regardless of the property's own default. The attribute is still set/removed alongside
      // it, unchanged from before, since other code (styling, tests) may read it directly.
      ;(widgetElement as unknown as Record<string, unknown>)[key] = value
      if (value) widgetElement.setAttribute(attr, '')
      else widgetElement.removeAttribute(attr)
    } else if (typeof value === 'object' && value !== null) {
      ;(widgetElement as unknown as Record<string, unknown>)[key] = value
    } else if (value != null) {
      widgetElement.setAttribute(attr, String(value))
    }
  })

  if (w.type === 'csc-order-form' && props['uom']) {
    const uomEl = document.createElement('cse-text')
    uomEl.setAttribute('slot', 'uom')
    uomEl.setAttribute('variant', 'caption-m')
    uomEl.textContent = String(props['uom'])
    widgetElement.appendChild(uomEl)
  }

  return widgetElement
}
